import { useEffect, useState } from 'react';
import { ChevronRight, Copy, Check, AlertTriangle, ChevronsDownUp, ChevronsUpDown, Info } from 'lucide-react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';
import type { DeliveryLog, DeliveryLogRow, DeliveryStatus } from '@/types/delivery';
import {
  destinationLabelFor,
  extractMessageView,
  normalizeJson,
  statusLabelFor,
  statusTooltipFor,
  stringifyBody,
  type ViewFilter,
} from './delivery-log-utils';

const TEXT_CLASS: Record<DeliveryStatus, string> = {
  delivered: 'text-foreground',
  no_response: 'text-foreground',
  rejected: 'text-foreground',
  not_delivered: 'text-foreground',
  skipped: 'text-muted-foreground',
};

const DOT_CLASS: Record<DeliveryStatus, string> = {
  delivered: 'bg-emerald-500',
  no_response: 'bg-red-500',
  rejected: 'bg-red-500',
  not_delivered: 'bg-red-500',
  skipped: 'bg-muted-foreground/40',
};

interface DeliveryRowProps {
  delivery: DeliveryLogRow;
  relatedLogs?: RelatedDeliveryView[];
  expanded: boolean;
  onToggle: () => void;
  viewFilter?: ViewFilter;
  channelType?: 'whatsapp' | 'instagram' | 'facebook';
}

interface RelatedDeliveryView {
  rowKey: string;
  receivedAt: string;
  status: DeliveryStatus;
  statusLabel: string;
}

function localDayDiff(a: Date, b: Date): number {
  const aStart = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bStart = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((bStart - aStart) / (24 * 60 * 60 * 1000));
}

function formatRelative(iso: string, now: number): string {
  const d = new Date(iso);
  const t = d.getTime();
  const diff = Math.max(0, now - t);
  const sec = Math.round(diff / 1000);
  if (sec < 5) return 'Just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const nowD = new Date(now);
  const dayDiff = localDayDiff(d, nowD);
  if (dayDiff <= 0) return `${hr}h ago`;
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  if (d.getFullYear() === nowD.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function TimeAgo({ iso, className }: { iso: string; className?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  const absolute = new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={className} />}>
        {formatRelative(iso, now)}
      </TooltipTrigger>
      <TooltipContent>{absolute}</TooltipContent>
    </Tooltip>
  );
}

export function DeliveryRow({
  delivery,
  relatedLogs = [],
  expanded,
  onToggle,
  viewFilter = 'all',
  channelType,
}: DeliveryRowProps) {
  const label = statusLabelFor(delivery);
  const tooltip = statusTooltipFor(delivery);
  const destinationLabel = destinationLabelFor(delivery);
  return (
    <div className="text-sm">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="grid w-full grid-cols-[1rem_0.75rem_minmax(0,1fr)_5rem] items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50 md:grid-cols-[1rem_0.75rem_8rem_14rem_1fr_5rem]"
      >
        <ChevronRight
          className={cn(
            'size-3.5 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )}
        />
        <span
          className={cn('size-2 rounded-full', DOT_CLASS[delivery.hookmyapp.status])}
          aria-hidden
        />
        <span
          className={cn(
            'truncate whitespace-nowrap font-medium',
            TEXT_CLASS[delivery.hookmyapp.status],
          )}
        >
          {label}
        </span>
        <div className="hidden min-w-0 items-center gap-1.5 md:flex">
          {destinationLabel ? (
            <span className="shrink-0 rounded border px-1.5 py-px text-xs text-muted-foreground">
              {destinationLabel}
            </span>
          ) : null}
          <span className="min-w-0 truncate text-muted-foreground">
            {delivery.hookmyapp.statusText}
          </span>
          <StatusInfoTooltip tooltip={tooltip} />
        </div>

        {/* sender hidden <md — the expanded detail shows a From line instead */}
        <div className="hidden min-w-0 items-center justify-end gap-1 md:flex">
          <span className="truncate font-mono text-xs text-muted-foreground/80">
            {delivery.sender ? `from ${delivery.sender}` : ''}
          </span>
          {delivery.sender && channelType === 'instagram' && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <span
                    aria-label="About this sender ID"
                    className="inline-flex shrink-0 items-center justify-center p-0 leading-none text-muted-foreground/60 hover:text-muted-foreground"
                  />
                }
              >
                <Info className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Meta doesn't include the sender's username in the Instagram webhook payload. We deliver the sender ID Meta sends so this matches what your app receives.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <TimeAgo
          iso={delivery.receivedAt}
          className="truncate text-right text-xs text-muted-foreground"
        />
      </button>

      {expanded ? (
        <div className="border-t bg-muted/30 px-4 py-3 md:pr-4 md:pl-[3.25rem]">
          <ExpandedDetail
            log={delivery}
            relatedLogs={relatedLogs}
            viewFilter={viewFilter}
          />
        </div>
      ) : null}
    </div>
  );
}

function ExpandedDetail({
  log,
  relatedLogs,
  viewFilter = 'all',
}: {
  log: DeliveryLog;
  relatedLogs: RelatedDeliveryView[];
  viewFilter?: ViewFilter;
}) {
  const destination = log.hookmyapp.destination;
  const showNoDestinationAlert =
    destination === null && log.hookmyapp.status === 'not_delivered';
  return (
    <div className="space-y-4">
      {log.sender ? (
        <div className="truncate font-mono text-xs text-muted-foreground/80 md:hidden">
          from {log.sender}
        </div>
      ) : null}
      {showNoDestinationAlert ? <NoDestinationAlert /> : null}
      <Section title={destination ? 'We sent it to your app' : 'What Meta sent us'}>
        {destination?.type === 'webhook' ? (
          <UrlLine method="POST" url={destination.url} />
        ) : destination?.type === 'cli' ? (
          <DestinationLine label={destination.label} />
        ) : destination?.type === 'subscription' ? (
          <>
            <DestinationLine label={destinationLabelFor(log)!} />
            <UrlLine method="POST" url={destination.url} />
          </>
        ) : null}
        <BodyView body={log.meta} viewFilter={viewFilter} />
      </Section>
      {destination ? (
        <Section title="Your app responded">
          <StatusLine
            status={log.hookmyapp.appResponse.status}
            durationMs={log.hookmyapp.appResponse.durationMs}
          />
          <BodyView body={log.hookmyapp.appResponse.body} />
        </Section>
      ) : null}
      {relatedLogs.length > 0 ? (
        <RelatedDeliveriesPanel related={relatedLogs} />
      ) : null}
    </div>
  );
}

export function NoDestinationAlert() {
  return (
    <Alert className="w-fit max-w-full items-start border-amber-500/30 bg-amber-500/5 sm:items-center [&>svg]:translate-y-0.5 [&>svg]:row-span-1 sm:[&>svg]:translate-y-0">
      <AlertTriangle className="text-amber-600 dark:text-amber-400" />
      <AlertDescription className="text-amber-700 dark:text-amber-300">
        We got this message but couldn&rsquo;t deliver it because no
        destination was configured at the time. Future messages will be
        delivered once one is set up.
      </AlertDescription>
    </Alert>
  );
}

function StatusInfoTooltip({ tooltip }: { tooltip: string | null }) {
  if (!tooltip) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            aria-label="More info"
            className="inline-flex shrink-0 items-center justify-center p-0 leading-none text-muted-foreground/60 hover:text-muted-foreground"
          />
        }
      >
        <Info className="size-3.5" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

function UrlLine({ method, url }: { method: string; url: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs">
      <span className="font-mono font-semibold">{method}</span>
      <span className="font-mono text-muted-foreground truncate" title={url}>
        {midEllipsisUrl(url)}
      </span>
    </div>
  );
}

function DestinationLine({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs">
      <span className="font-mono font-semibold">{label}</span>
    </div>
  );
}

function StatusLine({
  status,
  durationMs,
}: {
  status: number | null;
  durationMs: number | null;
}) {
  const ok = status != null && status >= 200 && status < 300;
  return (
    <div className="flex items-center gap-3 rounded-md border bg-background px-3 py-2 text-xs">
      <span
        className={cn(
          'font-mono font-semibold',
          ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
        )}
      >
        {status ?? '-'}
      </span>
      {durationMs != null ? (
        <span className="text-muted-foreground">{durationMs}ms</span>
      ) : null}
    </div>
  );
}

function RelatedDeliveriesPanel({
  related,
}: {
  related: RelatedDeliveryView[];
}) {
  return (
    <Section title="Related deliveries (same message)">
      <ul className="space-y-1 text-xs">
        {related.map((r) => (
          <li key={r.rowKey} className="flex items-center gap-2">
            <span
              className={cn('inline-block size-2 rounded-full', DOT_CLASS[r.status])}
              aria-hidden
            />
            <span>{r.statusLabel}</span>
            <span className="text-muted-foreground">
              {new Date(r.receivedAt).toLocaleTimeString()}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function BodyView({
  body,
  viewFilter = 'all',
}: {
  body: unknown;
  viewFilter?: ViewFilter;
}) {
  const [copied, setCopied] = useState(false);
  const [allCollapsed, setAllCollapsed] = useState(false);
  if (body == null || body === '') {
    return <div className="text-xs italic text-muted-foreground">(empty body)</div>;
  }
  const filteredView =
    viewFilter !== 'all' ? extractMessageView(body, viewFilter) : null;
  const parsed = filteredView ?? normalizeJson(body);
  const text = stringifyBody(parsed ?? body);
  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }
  return (
    <div className="group/json relative overflow-hidden rounded-md border bg-background">
      <div className="absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/json:opacity-100 focus-within:opacity-100">
        {parsed !== undefined ? (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setAllCollapsed((v) => !v)}
            aria-label={allCollapsed ? 'Expand all' : 'Collapse all'}
            title={allCollapsed ? 'Expand all' : 'Collapse all'}
          >
            {allCollapsed ? (
              <ChevronsUpDown className="size-3.5" />
            ) : (
              <ChevronsDownUp className="size-3.5" />
            )}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={handleCopy}
          aria-label="Copy"
          title="Copy"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </div>
      <div className="hookmyapp-json max-h-96 overflow-auto px-3 py-2 pr-16 text-xs leading-relaxed">
        {parsed !== undefined ? (
          <JsonView
            key={allCollapsed ? 'collapsed' : 'expanded'}
            src={parsed}
            collapsed={allCollapsed ? 1 : false}
            displaySize={false}
            displayArrayIndex={false}
            enableClipboard
          />
        ) : (
          <pre className="whitespace-pre-wrap font-sans">{String(body)}</pre>
        )}
      </div>
    </div>
  );
}

interface LogStreamProps {
  items: DeliveryLogRow[];
  relatedByKey: Record<string, RelatedDeliveryView[]>;
  viewFilter?: ViewFilter;
  channelType?: 'whatsapp' | 'instagram' | 'facebook';
}

export function LogStream({
  items,
  relatedByKey,
  viewFilter = 'all',
  channelType,
}: LogStreamProps) {
  return (
    <div className="divide-y">
      {items.map((d) => {
        const label = statusLabelFor(d);
        const tooltip = statusTooltipFor(d);
        return (
          <article key={d.rowKey} className="space-y-3 px-4 py-4 text-sm">
            <header className="grid grid-cols-[0.75rem_minmax(0,1fr)_5rem] items-center gap-3 md:grid-cols-[0.75rem_8rem_14rem_1fr_5rem]">
              <span
                className={cn('size-2 rounded-full', DOT_CLASS[d.hookmyapp.status])}
                aria-hidden
              />
              <span
                className={cn(
                  'truncate whitespace-nowrap font-medium',
                  TEXT_CLASS[d.hookmyapp.status],
                )}
              >
                {label}
              </span>
              <div className="col-start-2 col-span-2 row-start-2 flex min-w-0 items-center gap-1.5 md:col-span-1 md:col-start-auto md:row-start-auto">
                <span className="min-w-0 truncate text-muted-foreground">
                  {d.hookmyapp.statusText}
                </span>
                <StatusInfoTooltip tooltip={tooltip} />
              </div>
              {/* sender hidden <md — the expanded detail shows a From line instead */}
              <div className="hidden min-w-0 items-center justify-end gap-1 md:flex">
                <span className="truncate font-mono text-xs text-muted-foreground/80">
                  {d.sender ? `from ${d.sender}` : ''}
                </span>
                {d.sender && channelType === 'instagram' && (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span
                          aria-label="About this sender ID"
                          className="inline-flex shrink-0 items-center justify-center p-0 leading-none text-muted-foreground/60 hover:text-muted-foreground"
                        />
                      }
                    >
                      <Info className="size-3.5" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Meta doesn't include the sender's username in the Instagram webhook payload. We deliver the sender ID Meta sends so this matches what your app receives.
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <TimeAgo
                iso={d.receivedAt}
                className="truncate text-right text-xs text-muted-foreground"
              />
            </header>
            <ExpandedDetail
              log={d}
              relatedLogs={relatedByKey[d.rowKey] ?? []}
              viewFilter={viewFilter}
            />
          </article>
        );
      })}
    </div>
  );
}
